import { all, call, takeLatest, put } from 'redux-saga/effects';
import * as HttpStatus from 'http-status-codes';
// Untyped `.js` module (src/Config.js); `@/Config` resolves to the same file the
// `Config` alias points at, but is mapped in tsconfig paths. `serverUrl` is `any`.
import { serverUrl } from '@/Config';

import {
  CloudErrors,
  CloudActions,
  CloudConstants,
  UserServiceUrls,
  AuthServiceUrls,
  DefaultState,
  FlowjoCommons,
} from '@portal/portal-core';

import {
  createSignInPayload,
  createProductInfoHeader,
  getErrorCodeFromApiResponse,
  getErrorTextFromApiResponse,
  getUserIdFromApiResponse,
  getAuthTokenKey,
  setAuthJwtToken,
  getAuthJwtToken,
  removeAuthJwtToken,
} from './utils';

import * as Types from './types';
import * as Actions from './actions';

const { Net } = FlowjoCommons;

const { INVALID_CREDENTIALS } = CloudErrors.authErrorCodes;
const { UNVERIFIED_TOKEN_SENT, UNVERIFIED_TOKEN_NOT_SENT, UNVERIFIED_TOKEN_EXPIRED } =
  CloudErrors.emailUnverifiedErrorTypes;

// Config object threaded through the sign-in sagas.
interface SignInConfig {
  baseApiUrl: string;
  productName: string;
}

// The sign-in action dispatched into `callSignInSaga`.
interface SignInAction {
  type: string;
  payload: { email: string; password: string };
}

// Minimal shape of the `result` field on a sign-in API response that these sagas read.
interface SignInApiResult {
  jwt?: string | null;
  userId: string;
  emailVerificationStatus?: string;
}

// A single error entry on the response; shape mirrors the one `./utils` reads.
interface ApiErrorEntry {
  errorCode?: string;
  errorText?: string;
  url?: string;
}

// The parsed sign-in API response body. Kept assignable to the `ApiResponse` shape the
// `./utils` helpers expect (they access `errors` and `result`).
interface SignInApiResponse {
  errors: ApiErrorEntry[];
  result: SignInApiResult;
}

// Wrapped response shape returned by the email-send helpers / `Net.sendJson`.
interface SendJsonResult {
  code: number;
  data: unknown;
}

// `signInUnauthorized` takes no argument and always builds a fixed payload; the call
// sites below pass an error message that is ignored at runtime. Cast to preserve them.
const signInUnauthorizedWithMessage = Actions.signInUnauthorized as unknown as (
  message: string,
) => { type: string; payload: string };

export const expectedErrorTextForFailedSignIn = 'Sign In Failed: incorrect email or password.';
export const tooManyFailuresMessage = 'Too many failures. Sign in to resolve the issue.';
export const internalServerErrorMessage = 'ERROR: Internal Server Error';
export const unknownErrorMessage = 'ERROR: an unknown error has occured';

export const sendEmailVerificationEmail = async (
  cloudApiUrl: string,
  apiResponse: SignInApiResponse,
  email: string,
  productName: string,
): Promise<SendJsonResult> => {
  const emailVerificationApiUrl = cloudApiUrl + UserServiceUrls.emailVerificationPath();
  // `createProductInfoHeader` is typed `void` in `./utils`; pass its result through unchanged.
  const productInfoHeader = (await createProductInfoHeader(productName)) as unknown as
    | Record<string, string>
    | undefined;
  const userId = await getUserIdFromApiResponse(apiResponse);
  const payload = await CloudActions.emailVerificationTokenSend(userId, email);
  const { code, data } = (await Net.sendJson(
    emailVerificationApiUrl,
    payload,
    productInfoHeader,
  )) as SendJsonResult;

  return { code, data };
};

export const resendEmailVerificationEmail = async (
  cloudApiUrl: string,
  apiResponse: SignInApiResponse,
  email: string,
  productName: string,
): Promise<SendJsonResult> => {
  const emailVerificationApiUrl = cloudApiUrl + UserServiceUrls.emailVerificationPath();
  // `createProductInfoHeader` is typed `void` in `./utils`; pass its result through unchanged.
  const productInfoHeader = (await createProductInfoHeader(productName)) as unknown as
    | Record<string, string>
    | undefined;
  const userId = await getUserIdFromApiResponse(apiResponse);
  const payload = await CloudActions.emailVerificationTokenResend(userId, email);

  const { code, data } = (await Net.sendJson(
    emailVerificationApiUrl,
    payload,
    productInfoHeader,
  )) as SendJsonResult;

  return { code, data };
};

function* handleEmailSendResponse(
  code: number,
  userId: string,
  email: string,
): Generator<unknown, void, unknown> {
  if (code !== HttpStatus.OK) yield put(Actions.signInVerficationEmailFailedToSend());
  else yield put(Actions.signInPartialSuccessEmailNotVerified(userId, email));
}

function* handlePartialOkResponse(
  cloudApiUrl: string,
  apiResponse: SignInApiResponse,
  email: string,
  productName: string,
): Generator<unknown, void, unknown> {
  const { emailVerificationStatus } = apiResponse.result;
  const userId = (yield call(getUserIdFromApiResponse, apiResponse)) as string;

  if (emailVerificationStatus === UNVERIFIED_TOKEN_NOT_SENT) {
    const { code } = (yield call(
      sendEmailVerificationEmail,
      cloudApiUrl,
      apiResponse,
      email,
      productName,
    )) as SendJsonResult;
    yield call(handleEmailSendResponse, code, userId, email);
  } else if (emailVerificationStatus === UNVERIFIED_TOKEN_EXPIRED) {
    const { code } = (yield call(
      resendEmailVerificationEmail,
      cloudApiUrl,
      apiResponse,
      email,
      productName,
    )) as SendJsonResult;
    yield call(handleEmailSendResponse, code, userId, email);
  } else if (emailVerificationStatus === UNVERIFIED_TOKEN_SENT) {
    yield put(Actions.signInPartialSuccessEmailNotVerified(userId, email));
  }
}

function* handleUnauthorizedResponse(
  apiResponse: SignInApiResponse,
  // `config` is passed by the caller but unused here; kept to match the call site.
  _config?: SignInConfig,
): Generator<unknown, void, unknown> {
  const errorCode = (yield call(getErrorCodeFromApiResponse, apiResponse)) as string;

  if (errorCode && errorCode === INVALID_CREDENTIALS) {
    const errorMessageText = (yield call(getErrorTextFromApiResponse, apiResponse)) as string;
    yield put(signInUnauthorizedWithMessage(errorMessageText));
  } else {
    yield put(signInUnauthorizedWithMessage('something unexpected'));
  }
}

function* handleOkResponse(
  apiResponse: SignInApiResponse,
  email: string,
): Generator<unknown, void, unknown> {
  const { jwt, userId } = apiResponse.result;

  if (jwt) {
    // `getAuthTokenKey` may return undefined; `setAuthJwtToken` expects a string key.
    setAuthJwtToken(getAuthTokenKey() as string, jwt);
    yield put(Actions.signInSuccess(userId, email));

    /* TODO:
     this is a hack to kick the cloud watcher after a successful login,
     please remove this once the cloudwatcher is kicked by the consumer
     as a result of being authenticated
    */
    yield put(CloudActions.startCloudWatcher());
  }
}

export function* callSignInSaga(
  config: SignInConfig,
  action: SignInAction,
): Generator<unknown, void, unknown> {
  const { email, password } = action.payload;
  const url = `${config.baseApiUrl}${AuthServiceUrls.loginPath()}`;
  const { productName } = config;
  const headers = {
    [CloudConstants.CLIENT_HEADERS.productName]: productName,
  };

  const payload = createSignInPayload(email, password);

  const { code, data } = (yield call(Net.sendJson, url, payload, headers)) as {
    code: number;
    data: string;
  };

  const apiResponse = JSON.parse(data) as SignInApiResponse;

  if (code === HttpStatus.OK) {
    yield call(handleOkResponse, apiResponse, email);
  } else if (code === HttpStatus.UNAUTHORIZED) {
    yield call(handleUnauthorizedResponse, apiResponse, config);
  } else if (code === HttpStatus.PARTIAL_CONTENT) {
    yield call(handlePartialOkResponse, config.baseApiUrl, apiResponse, email, config.productName);
  } else if (code === HttpStatus.INTERNAL_SERVER_ERROR) {
    yield put(Actions.signInInternalServerError());
  } else {
    yield put(Actions.signInUnknownError());
  }
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchSignInSaga(config: SignInConfig): Generator<unknown, void, unknown> {
  yield takeLatest(Types.SIGN_IN_SAGA, callSignInSaga, config);
}

function* signOutSaga(
  // `config` is passed by the watcher but unused here; kept to match the call site.
  _config?: SignInConfig,
): Generator<unknown, void, unknown> {
  try {
    const jwt = getAuthJwtToken(getAuthTokenKey());
    if (jwt) {
      // `removeAuthJwtToken` now resolves the token key internally and takes no argument.
      removeAuthJwtToken();
    }
    /* @TODO:
     CloudActions.logout is a temporary hack to stop the cloud watcher if the token is no longer
     availble. Once cloudWatcher is moved to the user-client, remove the action from here.
    */
    yield put(CloudActions.logout());
    /*
      Always call the default state action upon sign out; this will cause all reducers handling
      the DEFAULT_STATE case to return to the pre-authenticated state (emptying sensative user
      data from memory).
    */
    yield put(DefaultState.defaultStateAction());
  } catch (error) {
    console.log(error);
  }
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchSignOutSaga(config: SignInConfig): Generator<unknown, void, unknown> {
  yield takeLatest(Types.SIGN_OUT_SAGA, signOutSaga, config);
}

// Requests a client config object from the service. Used by auth-client on initial page load to get
// clientside config values
export function getClientConfigAsync(): Promise<unknown> {
  return Net.getJson(AuthServiceUrls.clientConfigUrl(serverUrl));
}

/* istanbul ignore next reason: no rootSaga tests */
export default (config: SignInConfig = {} as SignInConfig) =>
  function* rootSaga(): Generator<unknown, void, unknown> {
    yield all([watchSignInSaga(config), watchSignOutSaga(config)]);
  };
